"use client";

import { AnimatePresence, motion } from "framer-motion";
import React, { useCallback, useRef } from "react";

import { cn } from "../utils";

// ---------------------------------------------------------------------------
// Role + style config
// ---------------------------------------------------------------------------

export type BubbleRole = "user" | "assistant" | "tool" | "system";

export type BubbleVariant =
  | "default" // role-derived colours
  | "error" // red tint
  | "editing"; // editing state (caller sets when in edit mode)

export interface BubbleStyleConfig {
  /** Classes applied to the bubble container. */
  bubble: string;
  /**
   * Flattened corner class — iOS 17-style: the corner nearest the alignment
   * edge gets a smaller radius.
   */
  corner: string;
}

/**
 * Derive the bubble visual style from role + variant.
 * This is the single source of truth for bubble colouring, shared by
 * every consumer (Playground ChatMessage, Vibes Message, etc.).
 */
export function getBubbleStyle(
  role: BubbleRole,
  variant: BubbleVariant = "default",
): BubbleStyleConfig {
  const isRightAligned = role === "user" || role === "tool";
  const cornerSide = isRightAligned ? "rounded-br-md" : "rounded-bl-md";

  if (variant === "error") {
    return {
      bubble: "bg-gray-100 text-red-500",
      corner: cornerSide,
    };
  }

  switch (role) {
    case "tool":
      return {
        bubble:
          variant === "editing"
            ? "border border-amber-300 bg-amber-100 text-gray-900"
            : "border border-amber-200 bg-amber-50 text-gray-900",
        corner: cornerSide,
      };

    case "user":
      return {
        bubble:
          variant === "editing"
            ? "bg-blue-100 text-gray-900"
            : "bg-blue-500 text-white",
        corner: cornerSide,
      };

    case "system":
      return {
        bubble: "bg-purple-50 border border-purple-200 text-gray-900",
        corner: cornerSide,
      };

    case "assistant":
    default:
      return {
        bubble: "bg-gray-100 text-gray-900",
        corner: cornerSide,
      };
  }
}

// ---------------------------------------------------------------------------
// Animation
// ---------------------------------------------------------------------------

const bubbleVariants = {
  initial: { opacity: 0, y: 8, scale: 0.97 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring" as const, stiffness: 400, damping: 28 },
  },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.12 } },
};

// ---------------------------------------------------------------------------
// Sub-types for hover-action slots
// ---------------------------------------------------------------------------

export interface HoverActionItem {
  key: string;
  node: React.ReactNode;
}

// ---------------------------------------------------------------------------
// ChatBubble props
// ---------------------------------------------------------------------------

export interface ChatBubbleProps {
  // ---- Identity / layout ----
  role: BubbleRole;

  /**
   * Override the automatic role-based alignment.
   * By default user/tool bubbles are right-aligned; assistant/system are left.
   */
  alignRight?: boolean;

  // ---- Visual variant ----
  variant?: BubbleVariant;

  /**
   * Override the entire bubble colour style — useful for special states such
   * as MCP approval responses.
   */
  bubbleClassName?: string;

  // ---- Size ----
  /**
   * Tighter max-width cap used in embedded / minimal contexts.
   * Defaults to "85%". Pass "95%" or a Tailwind class for minimal mode.
   */
  maxWidthClass?: string;

  // ---- Content ----
  /** The main bubble body — message content, edit form, streaming skeleton, etc. */
  children: React.ReactNode;

  // ---- Slots ----
  /**
   * Avatar rendered in a fixed-width column to the left (or right) of the bubble.
   * Only shown when the role is NOT right-aligned and when not in minimal mode.
   */
  avatar?: React.ReactNode;

  /**
   * Actions that float next to the bubble on hover.
   * Rendered as an absolutely-positioned strip that appears / disappears with
   * `group-hover/message` opacity transition.
   *
   * Pass an array of `{ key, node }` objects — order is respected.
   */
  hoverActions?: HoverActionItem[];

  /**
   * Content rendered below the bubble (e.g. latency / cost metadata).
   * Uses the same group-hover opacity reveal.
   */
  bottomMetadata?: React.ReactNode;

  // ---- Timestamp ----
  /** ISO / formatted timestamp string. Revealed on hover above the bubble. */
  timestamp?: string;
  hideTimestamp?: boolean;

  // ---- Animation ----
  /**
   * Unique key used by AnimatePresence. Pass `message.id` or similar.
   * If omitted the component still renders, just without keyed animations.
   */
  animateKey?: string;
  /** Skip the mount animation — useful for pre-existing history messages. */
  disableAnimation?: boolean;

  // ---- Misc ----
  className?: string;
}

// ---------------------------------------------------------------------------
// ChatBubble
// ---------------------------------------------------------------------------

/**
 * Shared chat-bubble primitive for PromptLayer apps.
 *
 * Encapsulates:
 * - Role-aware bubble styling (colours, iOS-style corner radius).
 * - Group-hover timestamp reveal above the bubble.
 * - Absolutely-positioned hover-action strip next to the bubble.
 * - Group-hover bottom-metadata reveal below the bubble.
 * - Mount / exit spring animation via framer-motion.
 * - Optional avatar column.
 *
 * Purely presentational — no store access, no side-effects.
 *
 * ### Minimal usage (Vibes / embed)
 * ```tsx
 * <ChatBubble role="user" animateKey={msg.id}>
 *   {msg.text}
 * </ChatBubble>
 * ```
 *
 * ### Full usage (Playground)
 * ```tsx
 * <ChatBubble
 *   role={message.role}
 *   variant={isError ? "error" : isEditing ? "editing" : "default"}
 *   animateKey={message.id}
 *   timestamp={createdAt}
 *   avatar={<MessageAvatar />}
 *   hoverActions={[
 *     { key: "copy", node: <CopyButton text={text} /> },
 *     { key: "actions", node: <ChatMessageActions {...} /> },
 *   ]}
 *   bottomMetadata={<LatencyCostRow message={message} />}
 * >
 *   <ContentBlocks content={contentBlocks} />
 * </ChatBubble>
 * ```
 */
export const ChatBubble = React.memo(({
  role,
  alignRight: alignRightProp,
  variant = "default",
  bubbleClassName,
  maxWidthClass = "max-w-[85%]",
  children,
  avatar,
  hoverActions,
  bottomMetadata,
  timestamp,
  hideTimestamp = false,
  animateKey,
  disableAnimation = false,
  className,
}: ChatBubbleProps) => {
  // Determine alignment: explicit prop wins; otherwise role-based.
  const isRightAligned =
    alignRightProp !== undefined
      ? alignRightProp
      : role === "user" || role === "tool";

  // Derive bubble colours only when no override is provided.
  const { bubble: defaultBubbleClasses, corner: cornerClass } = getBubbleStyle(
    role,
    variant,
  );
  const resolvedBubbleClasses = bubbleClassName ?? defaultBubbleClasses;

  // Animation guard — only animate once on first mount.
  const hasAnimated = useRef(false);
  const shouldAnimate = !disableAnimation && !hasAnimated.current;
  if (shouldAnimate) {
    hasAnimated.current = true;
  }

  const showTimestamp = !hideTimestamp && !!timestamp;
  const showHoverActions = !!hoverActions && hoverActions.length > 0;
  const showBottomMetadata = !!bottomMetadata;
  const showAvatar = !!avatar && !isRightAligned;

  const inner = (
    <motion.div
      key={animateKey}
      variants={bubbleVariants}
      initial={shouldAnimate ? "initial" : false}
      animate="animate"
      exit="exit"
      className={cn(
        "group/message flex gap-2 px-2",
        maxWidthClass,
        isRightAligned ? "flex-row-reverse self-end" : "flex-row self-start",
        className,
      )}
    >
      {/* ---- Avatar column (left / right depending on alignment) ---- */}
      {showAvatar && (
        <div className={cn("flex w-7 shrink-0 flex-col justify-end")}>
          <div
            className={cn(
              "sticky bottom-2",
              showBottomMetadata ? "mb-6" : "mb-0",
            )}
          >
            {avatar}
          </div>
        </div>
      )}

      {/* ---- Bubble column ---- */}
      <div className="flex min-w-0 flex-col">
        {/* Timestamp — fades in on group hover */}
        {showTimestamp && (
          <div
            className={cn(
              "flex px-1 pb-0.5 text-[10px] text-gray-400",
              "opacity-0 transition-opacity duration-200 group-hover/message:opacity-100",
              isRightAligned ? "justify-end" : "justify-start",
            )}
          >
            <span>{timestamp}</span>
          </div>
        )}

        {/* ---- Bubble + floating hover actions ---- */}
        <div className="relative">
          {/* The bubble itself */}
          <div
            className={cn(
              "relative flex min-w-0 flex-col gap-2 rounded-2xl px-3 py-2",
              cornerClass,
              resolvedBubbleClasses,
            )}
          >
            {children}
          </div>

          {/* Hover actions — floated outside the bubble */}
          {showHoverActions && (
            <div
              className={cn(
                "pointer-events-none absolute top-1.5 flex items-start",
                isRightAligned ? "right-full" : "left-full",
              )}
            >
              <div
                className={cn(
                  "pointer-events-auto flex items-center gap-0.5",
                  "opacity-0 transition-opacity duration-200 group-hover/message:opacity-100",
                  isRightAligned ? "pr-1" : "pl-1",
                )}
              >
                {hoverActions.map(({ key, node }) => (
                  <React.Fragment key={key}>{node}</React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bottom metadata — fades in on group hover */}
        {showBottomMetadata && (
          <div
            className={cn(
              "mt-1 px-1",
              "opacity-0 transition-opacity duration-200 group-hover/message:opacity-100",
            )}
          >
            {bottomMetadata}
          </div>
        )}
      </div>
    </motion.div>
  );

  // Wrap with AnimatePresence only when we have a key to track.
  if (animateKey) {
    return (
      <AnimatePresence key={animateKey} mode="popLayout">
        {inner}
      </AnimatePresence>
    );
  }

  return inner;
});

// ---------------------------------------------------------------------------
// HoverActionButton — a convenience wrapper for the most common action shape:
// a small square icon button that participates in the hover-action strip.
// ---------------------------------------------------------------------------

export interface HoverActionButtonProps {
  onClick: () => void;
  "aria-label": string;
  tooltip?: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export const HoverActionButton = React.memo(({
  onClick,
  "aria-label": ariaLabel,
  children,
  className,
  disabled = false,
}: HoverActionButtonProps) => {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick();
    },
    [onClick],
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={ariaLabel}
      disabled={disabled}
      className={cn(
        "flex h-6 w-6 items-center justify-center rounded-md",
        "text-gray-400 hover:bg-gray-200 hover:text-gray-600",
        "disabled:pointer-events-none disabled:opacity-40",
        "focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400",
        className,
      )}
    >
      {children}
    </button>
  );
});
